import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AlertService,
  ButtonColors,
  ButtonModule,
  CheckboxModule,
  LayerService,
  TextfieldModule,
} from '@ed---interne/ng-uui-components';
import { Store } from '@ngxs/store';
import { Subject, Subscription } from 'rxjs';
import { UserPropertyType } from '../../../shared/all.enums';
import { Org, Property, User } from '../../../shared/all.types';
import { ORG_TYPE_MAP } from '../../../shared/enums/org.enums';
import { ENERGY_SHARE_MAP, EnergyShare, NrjType } from '../../../shared/enums/property.enums';
import { clearValidators, markAllChildrenAsDirty } from '../../../shared/form.helpers';
import { hasGas } from '../../../shared/property.helpers';
import { GetEnergy, GetProperty } from '../../../state/property.state';
import { EnergyService } from '../energy.service';

export interface ConsentParams {
  nrjType: NrjType;
  property: Property;
  loggedUser: User;
  sub: Subject<void>;
  isNewUser: boolean;
  step: number;
  org?: Org | null;
  token?: string;
}

@Component({
  standalone: true,
  imports: [ButtonModule, CheckboxModule, FormsModule, ReactiveFormsModule, CommonModule, TextfieldModule],
  selector: 'cil-energy-activate-consent',
  templateUrl: './energy-activate-consent.component.html',
  styleUrls: ['./energy-activate-consent.component.scss'],
  providers: [EnergyService, LayerService],
})
export class EnergyActivateConsentComponent implements OnInit, OnDestroy {
  /**
   * Display of the current page
   * 0 = energy meter
   * 1 = consent
   * 2 = permissions
   * 3 = confirmation message
   */
  public step = 0;
  public showDataInfo = false;
  public isLoading = false;
  public nrjType: NrjType = NrjType.ENEDIS;
  public property: Property;
  public loggedUser: User;
  public name = '';
  public isNewUser: boolean;
  public isInactiveUser: boolean;
  public NrjType = NrjType;
  public ButtonColors = ButtonColors;
  public EnergyShare = EnergyShare;
  public energyShareMap = ENERGY_SHARE_MAP;
  public orgTypeMap = ORG_TYPE_MAP;
  public org: Org | null = null;
  public token = '';
  public sub: Subject<void>;
  public isEditing = false;

  public activateConsentFormGroup = this.formBuilder.group({
    meter: this.formBuilder.group({
      meterId: ['', [Validators.required, Validators.maxLength(14), Validators.minLength(14)]],
    }),
    consent: this.formBuilder.group({
      firstConsent: [false, [Validators.requiredTrue]],
      secondConsent: [false, [Validators.requiredTrue]],
      thirdConsent: [false, [Validators.requiredTrue]],
    }),
    share: this.formBuilder.group({
      shareOptions: this.formBuilder.array([]),
    }),
  });
  public shareOptionsArray: FormArray;
  private _subscriptions: Subscription[] = [];

  @Input() public set params(params: ConsentParams) {
    this.nrjType = params.nrjType;
    this.property = params.property;
    this.loggedUser = params.loggedUser;
    this.name = [this.loggedUser.firstName, this.loggedUser.lastName].filter((name) => !!name).join(' ');
    this.sub = params.sub;
    this.isNewUser = params.isNewUser;
    this.step = params.step || 0;
    this.org = params.org || null;
    this.token = params.token || '';

    this.showDataInfo = false;

    this.activateConsentFormGroup.get('meter')?.patchValue({
      meterId:
        (this.nrjType === NrjType.GRDF ? this.property.gasState?.meterId : this.property.electricityState?.meterId) ||
        '',
    });

    if (this.nrjType === NrjType.GRDF) {
      clearValidators(this.activateConsentFormGroup, 'consent', 'secondConsent');
      clearValidators(this.activateConsentFormGroup, 'consent', 'thirdConsent');
    }

    if (!this.isNewUser || this.nrjType === NrjType.ENEDIS) {
      this.shareOptionsArray = this.activateConsentFormGroup.get('share')?.get('shareOptions') as FormArray;

      for (const up of this.property.userProperties!) {
        if (up.user!.id === this.loggedUser.id) {
          continue;
        }

        this.shareOptionsArray.push(
          this.formBuilder.group({
            energyShare: EnergyShare.None,
            userId: up.user!.id,
            userName: up.user!.firstName + ' ' + up.user!.lastName,
            isOwner: up.type === UserPropertyType.Owner,
            orgName: up.org?.name || '',
            orgType: up.org?.type || null,
          }),
        );
      }
    }

    if (this.token) {
      const energyState = this.nrjType === NrjType.ENEDIS ? this.property.electricityState : this.property.gasState;

      if (energyState.grantedConsent && this.step === 1) {
        this.step = 2;
        this.isEditing = true;

        for (const up of this.property.userProperties!) {
          const formGroup = this.shareOptionsArray.controls.find(
            (control) => control.get('userId')?.value === up.userId,
          ) as FormGroup;

          if (formGroup) {
            formGroup.patchValue({
              energyShare: this.nrjType === NrjType.ENEDIS ? up.electricityShareRight : up.gasShareRight,
            });
          }
        }
      }
    }
  }

  @Output() public onEnd = new EventEmitter<void>();

  constructor(
    private readonly alertService: AlertService,
    private readonly energyService: EnergyService,
    private readonly formBuilder: FormBuilder,
    private readonly store: Store,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
  ) {}

  public ngOnInit(): void {
    if (this.nrjType === NrjType.ENEDIS && this.activatedRoute.snapshot.queryParamMap.get('consent') === 'true') {
      void this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { consent: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  public ngOnDestroy(): void {
    for (const sub of this._subscriptions) {
      sub.unsubscribe();
    }
  }

  public getAddressLabel(): string {
    return `${this.property.address?.housenumber} ${this.property.address?.street} ${this.property.address?.postcode} ${this.property.address?.city}`;
  }

  public nextStep(currentStepName: string): void {
    this.activateConsentFormGroup.get(currentStepName)?.markAsDirty();
    markAllChildrenAsDirty(this.activateConsentFormGroup.get(currentStepName) as FormGroup);

    if (!this.activateConsentFormGroup.get(currentStepName)?.valid) {
      return;
    }

    this.step++;
  }

  public previousStep(): void {
    this.step--;

    if (this.step === 1) {
      this.activateConsentFormGroup.get('consent')?.patchValue({
        firstConsent: false,
        secondConsent: false,
        thirdConsent: false,
      });
    }
  }

  public accept(): void {
    if (this.isEditing) {
      this.editShareOptions();
    } else {
      this.activate();
    }
  }

  public activate(): void {
    this.isLoading = true;
    const sub = this.energyService
      .activateConsent(
        this.property.id!,
        this.nrjType,
        this.activateConsentFormGroup.get('meter')?.get('meterId')?.value || '',
        this.activateConsentFormGroup.get('share')?.get('shareOptions')?.value || [],
        this.org?.id,
        this.token,
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.store.dispatch([new GetProperty(this.property.id!), new GetEnergy()]).subscribe({
            next: () => this.sub.next(),
            error: () => this.sub.next(),
          });
          // this.alertService.valid('Consentement', 'Recueil activé !');

          this.nextStep('share');
        },
        error: (err) => {
          this.isLoading = false;
          this.alertService.error('Consentement', err.error.content?.reason);
        },
      });

    this._subscriptions.push(sub);
  }

  public editShareOptions(): void {
    const sub = this.energyService
      .editShareOptions(
        this.property.id!,
        this.nrjType,
        this.activateConsentFormGroup.get('share')?.get('shareOptions')?.value || [],
        this.property.loggedUserProperty?.orgId,
        this.token,
      )
      .subscribe({
        next: () => {
          this.store.dispatch([new GetProperty(this.property.id!)]);
          this.alertService.valid('Consentement', 'Partage modifié !');
        },
        error: (err) => {
          this.alertService.error('Consentement', err.error.content?.reason);
        },
      });

    this._subscriptions.push(sub);
  }

  public deny(): void {
    const sub = this.energyService
      .denyConsent(
        this.property.id!,
        this.nrjType,
        this.activateConsentFormGroup.get('meter')?.get('meterId')?.value || '',
        this.org?.id,
        this.token,
      )
      .subscribe({
        next: () => {
          this.sub.next();

          if (this.token) {
            this.step = 5;
          } else {
            this.store.dispatch(new GetProperty(this.property.id!));
            this.alertService.valid('Consentement', 'Refusé');
            this.onEnd.emit();
          }
        },
        error: (err) => {
          this.alertService.error('Consentement', err.error.content?.reason);
        },
      });

    this._subscriptions.push(sub);
  }

  public revoke(): void {
    const sub = this.energyService.revokeConsent(this.property.id!, this.nrjType, this.org?.id, this.token).subscribe({
      next: () => {
        this.step = 6;
      },
      error: (err) => {
        this.alertService.error('Consentement', err.error.content?.reason);
      },
    });

    this._subscriptions.push(sub);
  }

  public signup(): void {
    this.router.navigate(['/signup'], {
      queryParams: { token: this.loggedUser.invitationToken },
      queryParamsHandling: 'merge',
    });
  }

  public end(): void {
    if (this.isNewUser && hasGas(this.property) && this.nrjType === NrjType.ENEDIS) {
      this.nrjType = NrjType.GRDF;
      this.step = 0;

      return;
    }

    this.onEnd.emit();
  }

  public close(): void {
    this.onEnd.emit();
  }

  public getEndButtonLabel(): string {
    if (this.isNewUser) {
      if (hasGas(this.property) && this.nrjType === NrjType.ENEDIS) {
        return 'Activer le recueil pour le gaz';
      }

      return 'Découvrir PHILOGI';
    }

    return 'Terminer';
  }

  public getMeterId(): string {
    const formValue = this.activateConsentFormGroup.get('meter')?.get('meterId')?.value;

    if (formValue) {
      return formValue;
    }

    switch (this.nrjType) {
      case NrjType.ENEDIS:
        return this.property.electricityState.meterId || '';
      case NrjType.GRDF:
        return this.property.gasState.meterId || '';
      default:
        return '';
    }
  }

  public toggleDataInfo(): void {
    this.showDataInfo = !this.showDataInfo;
  }

  public getActivateConsentTitle(): string {
    if (this.isNewUser) {
      return 'Bienvenue sur votre espace Philogi !';
    }

    return this.nrjType === NrjType.GRDF
      ? 'Quel est votre point de livraison de gaz naturel ?'
      : "Quel est votre point de livraison d'électricité ?";
  }
}
